import React, { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchCertificateFromURI, CertificateData } from '../services/web3Service';
import './CertificateHistoryPage.css';

// A small, reusable component to display a certificate's data.
// This was moved from CertificateDetails.tsx
const CertificateCard: FC<{ certificate: CertificateData, uri: string, t: any }> = ({ certificate, uri, t }) => (
    <div className="queried-info" style={{ marginTop: '1rem', border: '1px solid #ccc', padding: '10px', borderRadius: '4px' }}>
        {certificate.image && (
            <img
                src={certificate.image}
                alt={certificate.name}
                style={{ maxWidth: '150px', borderRadius: '8px', marginBottom: '1rem' }}
            />
        )}
        <p><strong>{t('form.nameLabel')}:</strong> {certificate.name}</p>
        <p><strong>{t('form.descriptionLabel')}:</strong> {certificate.description}</p>
        {certificate.attributes && certificate.attributes.length > 0 && (
            <div className="attributes-section">
                <strong>{t('itemDetails.attributesTitle')}:</strong>
                <ul>
                    {certificate.attributes.map((attr, index) => (
                        <li key={index}><strong>{attr.trait_type}:</strong> {String(attr.value)}</li>
                    ))}
                </ul>
            </div>
        )}
        <p style={{ fontSize: '0.8em', wordBreak: 'break-all' }}><strong>URI:</strong> <a href={uri} target="_blank" rel="noopener noreferrer">{t('itemDetails.ipfsLink')}</a></p>
    </div>
);

interface CertificateHistoryPageProps {
    tokenId: string;
    initialUri: string;
    historicalUris: string[];
    onBack: () => void;
}

const CertificateHistoryPage: FC<CertificateHistoryPageProps> = ({
    tokenId,
    initialUri,
    historicalUris,
    onBack
}) => {
    const { t } = useTranslation();
    const [showInitial, setShowInitial] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [initialCertificate, setInitialCertificate] = useState<CertificateData | null>(null);
    const [historicalCertificates, setHistoricalCertificates] = useState<{ cert: CertificateData, uri: string }[]>([]);
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const handleQueryInitial = async () => {
        // If already shown, do nothing.
        if (showInitial) return;

        setShowInitial(true);
        setShowHistory(false); // Hide the other section

        if (!initialCertificate) {
            setIsLoadingInitial(true);
            setFetchError(null);
            try {
                const certData = await fetchCertificateFromURI(initialUri);
                setInitialCertificate(certData);
            } catch (err) {
                console.error(err);
                setFetchError(t('pages.history.fetchInitialError'));
            } finally {
                setIsLoadingInitial(false);
            }
        }
    };

    const handleQueryHistory = async () => {
        // If already shown, do nothing.
        if (showHistory) return;

        setShowHistory(true);
        setShowInitial(false); // Hide the other section

        if (historicalCertificates.length === 0 && historicalUris.length > 0) {
            setIsLoadingHistory(true);
            setFetchError(null);
            try {
                const certsData = await Promise.all(
                    historicalUris.map(async (uri) => ({ cert: await fetchCertificateFromURI(uri), uri: uri }))
                );
                setHistoricalCertificates(certsData);
            } catch (err) {
                console.error(err);
                setFetchError(t('pages.history.fetchHistoryError'));
            } finally {
                setIsLoadingHistory(false);
            }
        }
    };

    return (
        <div className="certificate-history-page-container">
            <button onClick={onBack} className="back-button action-button">{t('pages.history.backToDetails')}</button>
            <h2>{t('pages.history.title', { tokenId })}</h2>

            <div className="certificate-actions" style={{ marginTop: '1rem', display: 'flex', gap: '10px' }}>
                <button onClick={handleQueryInitial} className="action-button" disabled={showInitial}>
                    {t('pages.history.queryInitial')}
                </button>
                <button
                    onClick={handleQueryHistory}
                    className="action-button"
                    disabled={showHistory || !historicalUris || historicalUris.length === 0}
                >
                    {t('pages.history.queryPrevious', { count: historicalUris.length })}
                </button>
            </div>
            {fetchError && <p style={{ color: 'red', marginTop: '1rem' }}>{fetchError}</p>}

            {showInitial && (
                <>
                    <h4 style={{ marginTop: '1rem' }}>{t('pages.history.initialTitle')}</h4>
                    {isLoadingInitial && <p>{t('loading')}</p>}
                    {initialCertificate && <CertificateCard certificate={initialCertificate} uri={initialUri} t={t} />}
                </>
            )}

            {showHistory && historicalUris && historicalUris.length > 0 && (
                <>
                    <h4 style={{ marginTop: '1rem' }}>{t('pages.history.historicalTitle')}</h4>
                    {isLoadingHistory && <p>{t('loading')}</p>}
                    {historicalCertificates.length > 0 && (
                        <div className="historical-uris">
                            {historicalCertificates.map(({ cert, uri }, index) => (
                                <CertificateCard key={index} certificate={cert} uri={uri} t={t} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CertificateHistoryPage;